import React, { useEffect } from "react";
import Modal from "react-modal";
import { useTranslation } from "react-i18next";

interface Props {
  children: any;
}

interface ModalContextData {
  title?: string;
  message?: string;
  closable?: boolean;
  showCancelButton?: boolean;
  buttonTitle?: string;
  buttonClasses?: string;
  customContent?: React.ReactNode;
  size?: "xs" | "sm" | "md" | "lg";
  callback?: () => void;
}

export interface IModalContext {
  openModal: (data: ModalContextData) => void;
  closeModal: () => void;
}

export const ModalContext = React.createContext<IModalContext>({
  openModal: () => {
  },
  closeModal: () => {
  },
});

export const ModalContextProvider = ({ children }: Props) => {
  // -----------------------------------------------------------------
  // L o c a l   v a r s
  // -----------------------------------------------------------------
  // Access to i18n instance that you may use to translate your content.
  const { t } = useTranslation();

  // -----------------------------------------------------------------
  // N a v i g a t i o n   v a r s
  // -----------------------------------------------------------------

  // -----------------------------------------------------------------
  // S t a t e
  // -----------------------------------------------------------------
  const [isOpen, setOpen] = React.useState<boolean>(false);
  const [closable, setClosable] = React.useState<boolean>(true);
  const [showCancelButton, setShowCancelButton] = React.useState<boolean>(true);
  const [buttonTitle, setButtonTitle] = React.useState<string | undefined>(undefined);
  const [buttonClasses, setButtonClasses] = React.useState<string | undefined>(undefined);
  const [title, setTitle] = React.useState<string | undefined>(undefined);
  const [message, setMessage] = React.useState<string | undefined>(undefined);
  const [customContent, setCustomContent] = React.useState<React.ReactNode | undefined>(undefined);
  const [size, setSize] = React.useState<string>("sm");
  const [callback, setCallback] = React.useState<(() => void) | undefined>(undefined);

  // -----------------------------------------------------------------
  // R e f s  (DOM)
  // -----------------------------------------------------------------

  // -----------------------------------------------------------------
  // u s e S e l e c t o r   m e t h o d s  (redux)
  // -----------------------------------------------------------------

  // -----------------------------------------------------------------
  // W o r k i n g   m e t h o d s
  // -----------------------------------------------------------------
  /**
   * Opens the modal and set the state
   * @function
   *
   * @param data
   */
  const openModal = (data: ModalContextData): void => {
    setOpen(true);

    if (data.title) {
      setTitle(data.title);
    }

    if (data.message) {
      setMessage(data.message);
    }

    if (data.customContent) {
      setCustomContent(data.customContent);
    }

    if (data.size) {
      setSize(data.size);
    }

    if (data.callback) {
      setCallback(data.callback);
    }

    if (data.buttonTitle) {
      setButtonTitle(data.buttonTitle);
    }

    if (data.buttonClasses) {
      setButtonClasses(data.buttonClasses);
    }

    if (typeof data.showCancelButton !== "undefined") {
      setShowCancelButton(data.showCancelButton);
    }

    setClosable(!!data.closable);
  };

  /**
   * Close the modal and update the state
   * @function
   */
  const closeModal = (): void => {
    setOpen(false);
    setTitle(undefined);
    setMessage(undefined);
    setCustomContent(undefined);
    setSize("sm");
    setCallback(undefined);
    setButtonTitle(undefined);
    setButtonClasses(undefined);
    setShowCancelButton(true);
    setClosable(true);
  };

  // -----------------------------------------------------------------
  // R e n d e r   m e t h o d s
  // -----------------------------------------------------------------

  const renderModal = (): React.ReactNode => {
    return (
      <>
        <Modal
          ariaHideApp={false}
          isOpen={isOpen}
          shouldCloseOnEsc={closable}
          shouldCloseOnOverlayClick={closable}
          onRequestClose={closeModal}
          className={`meetersModal ${size}`}
          contentLabel={title}
        >
          {!customContent && (
            <>
              <h2>{title}</h2>
              <p>{message}</p>
            </>
          )}

          {!!customContent && customContent}

          <br/>
          <div className={"clearfix"}>
            {showCancelButton && (
              <button
                className={`btn btn-default pull-${callback ? "left" : "right"}`}
                onClick={closeModal}
              >
                {t(`modal.${callback ? "cancel" : "ok"}`)}
              </button>
            )}
            {callback && (
              <button
                className={`btn pull-right ${buttonClasses || "btn-primary"}`}
                onClick={() => {
                  callback();
                  closeModal();
                }}
              >
                {buttonTitle || t("modal.proceed")}
              </button>
            )}
          </div>
        </Modal>
      </>
    );
  };

  // -----------------------------------------------------------------
  // L i f e c y c l e
  // -----------------------------------------------------------------
  /**
   * This method is called the first time the component is mounted
   * @function
   */
  const init = (): void => {
    // init component
    console.log("[Modal provider] init");
  };

  /**
   * This method is called when the component is unmounted
   * @function
   */
  const destroy = (): void => {
    // destroy component
    console.log("[Modal provider] destroy");
    // destroy all
  };

  // -----------------------------------------------------------------
  // u s e E f f e c t   m e t h o d s
  // -----------------------------------------------------------------
  /**
   * This hook is called once when the component is mounted
   */
  useEffect(() => {
    init();
    return () => {
      destroy();
    };
  }, []);

  // -----------------------------------------------------------------
  // T e m p l a t e
  // -----------------------------------------------------------------
  return (
    <ModalContext.Provider
      value={{
        openModal,
        closeModal,
      }}
    >
      {children}
      {renderModal()}
    </ModalContext.Provider>
  );
};
